import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { ContactKind, CustomFieldDef, NewContact } from '@personical/cc-shared-types';
import { ContactForm } from '../components/ContactForm.js';
import { useApi } from '../components/api-context.js';
import { parsePastedContact } from '../parse.js';

const EMPTY: NewContact = {
  displayName: '',
  jobTitle: '',
  company: '',
  website: '',
  emails: [],
  phoneNumbers: [],
  notes: '',
  tags: [],
  callTreatment: 'normal',
  visibility: 'shared',
  kind: 'person',
  custom: {}
};

export function ContactNewPage() {
  const api = useApi();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [customFields, setCustomFields] = useState<CustomFieldDef[]>([]);
  const [pasteText, setPasteText] = useState('');
  const [pasted, setPasted] = useState<NewContact | null>(null);
  const [pasteVersion, setPasteVersion] = useState(0);
  const [pasteHint, setPasteHint] = useState<string | null>(null);
  const [showPaste, setShowPaste] = useState(false);
  const [scanning, setScanning] = useState(false);
  const scanRef = useRef<HTMLInputElement>(null);

  // The toolbar's "New" buttons pass ?kind=company or ?kind=person so
  // the right form is shown and the right grid is the landing page on
  // submit/cancel. Default to person for callers (e.g. Chrome extension
  // context menu) that don't specify.
  const kind: ContactKind =
    searchParams.get('kind') === 'company' ? 'company' : 'person';
  const isCompany = kind === 'company';

  const initial = useMemo<NewContact>(
    () => ({ ...EMPTY, ...pasted, kind }),
    [pasted, kind]
  );
  const indexHref = isCompany ? '/companies' : '/people';

  // The Chrome extension's "Save selection as a Cloud Contact" context
  // menu hands its selected text in via ?paste=… so the form pre-fills.
  useEffect(() => {
    const incoming = searchParams.get('paste');
    if (!incoming) return;
    applyPaste(incoming);
    setShowPaste(true);
    setPasteText(incoming);
    const next = new URLSearchParams(searchParams);
    next.delete('paste');
    setSearchParams(next, { replace: true });
    // run once on mount only — applyPaste / setSearchParams / searchParams
    // deliberately omitted to avoid a re-paste loop.
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .getSettings()
      .then((s) => {
        if (!cancelled) setCustomFields(s.customFields ?? []);
      })
      .catch(() => {
        if (!cancelled) setCustomFields([]);
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  async function submit(input: NewContact) {
    const created = await api.create({ ...input, kind });
    navigate(`${indexHref}/${created.id}`);
  }

  function applyPaste(text: string) {
    const parsed = parsePastedContact(text);
    if (
      !parsed.displayName &&
      (parsed.emails?.length ?? 0) === 0 &&
      (parsed.phoneNumbers?.length ?? 0) === 0
    ) {
      setPasteHint('Could not extract anything useful. Try editing the text.');
      return;
    }
    setPasted({ ...EMPTY, ...parsed });
    setPasteVersion((v) => v + 1);
    setPasteHint(
      `Filled: ${[
        parsed.displayName && 'name',
        parsed.company && 'company',
        parsed.jobTitle && 'title',
        parsed.emails?.length && `${parsed.emails.length} email(s)`,
        parsed.phoneNumbers?.length && `${parsed.phoneNumbers.length} phone(s)`,
        parsed.website && 'website'
      ]
        .filter(Boolean)
        .join(', ')}`
    );
  }

  async function pasteFromClipboard() {
    try {
      const text = await navigator.clipboard.readText();
      setPasteText(text);
      applyPaste(text);
    } catch {
      setPasteHint('Clipboard read denied — paste into the box manually.');
      setShowPaste(true);
    }
  }

  async function scanCard(file: File) {
    setScanning(true);
    setPasteHint(null);
    try {
      const parsed = await api.scanBusinessCard(file);
      // Anything the card gave us is worth keeping, even without a name.
      if (
        !parsed.displayName &&
        !parsed.company &&
        !parsed.jobTitle &&
        !parsed.department &&
        !parsed.address &&
        !parsed.website &&
        (parsed.emails?.length ?? 0) === 0 &&
        (parsed.phoneNumbers?.length ?? 0) === 0
      ) {
        setPasteHint('Scan returned no usable fields. Try a clearer photo.');
        return;
      }
      setPasted({ ...EMPTY, ...parsed });
      setPasteVersion((v) => v + 1);
      setPasteHint(
        `Scanned: ${[
          parsed.displayName && 'name',
          parsed.company && 'company',
          parsed.jobTitle && 'title',
          parsed.department && 'department',
          parsed.address && 'address',
          parsed.emails?.length && `${parsed.emails.length} email(s)`,
          parsed.phoneNumbers?.length && `${parsed.phoneNumbers.length} phone(s)`
        ]
          .filter(Boolean)
          .join(', ')}`
      );
    } catch (err) {
      setPasteHint(`Scan failed: ${(err as Error).message}`);
    } finally {
      setScanning(false);
      if (scanRef.current) scanRef.current.value = '';
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <h2 className="text-2xl font-semibold">{isCompany ? 'New company' : 'New person'}</h2>
        <div className="flex gap-2 w-full sm:w-auto">
          <input
            ref={scanRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
            capture="environment"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void scanCard(file);
            }}
          />
          <button
            type="button"
            onClick={() => scanRef.current?.click()}
            disabled={scanning}
            className="btn-secondary flex-1 sm:flex-initial px-2 sm:px-3 py-2 text-sm font-medium whitespace-nowrap disabled:opacity-50"
            title="Photograph a business card; we'll extract the fields"
          >
            {scanning ? (
              'Scanning…'
            ) : (
              <>
                <span className="sm:hidden">Scan card</span>
                <span className="hidden sm:inline">Scan business card</span>
              </>
            )}
          </button>
          <button
            type="button"
            onClick={pasteFromClipboard}
            className="btn-secondary flex-1 sm:flex-initial px-2 sm:px-3 py-2 text-sm font-medium whitespace-nowrap"
          >
            <span className="sm:hidden">Clipboard</span>
            <span className="hidden sm:inline">Paste from clipboard</span>
          </button>
          <button
            type="button"
            onClick={() => setShowPaste((v) => !v)}
            className="btn-secondary flex-1 sm:flex-initial px-2 sm:px-3 py-2 text-sm font-medium whitespace-nowrap"
          >
            <span className="sm:hidden">{showPaste ? 'Hide' : 'Paste text'}</span>
            <span className="hidden sm:inline">{showPaste ? 'Hide paste box' : 'Paste text…'}</span>
          </button>
        </div>
      </div>

      {showPaste && (
        <section className="surface rounded-2xl shadow p-6 space-y-3">
          <p className="text-sm text-mid">
            Paste an email signature, business card text, or anything else with a name,
            phone, and email. We'll extract what we can.
          </p>
          <textarea
            rows={6}
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder={'Ada Lovelace\nAnalyst, Analytical Engine Co.\nada@analytical.test\n+44 20 7946 0000'}
            className="input w-full font-mono text-xs"
          />
          <div className="flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => applyPaste(pasteText)}
              disabled={!pasteText.trim()}
              className="btn-primary px-3 py-2 text-sm font-medium disabled:opacity-50"
            >
              Extract
            </button>
            {pasteHint && <span className="text-sm text-mid">{pasteHint}</span>}
          </div>
        </section>
      )}

      <div className="surface rounded-2xl shadow p-6">
        <ContactForm
          key={`new-${pasteVersion}`}
          submitLabel="Create"
          customFields={customFields}
          api={api}
          initial={initial}
          onSubmit={submit}
          onCancel={() => navigate(indexHref)}
        />
      </div>
    </div>
  );
}
